import { useInlineRename } from '@common/Copilot/CopilotAgentChat/agent-v4/InlineRename';
import { MessageChatSquareIcon, PlusIcon, SearchMdIcon } from '@prophecy/icons';
import { ICON_CLASS } from '@prophecy/icons/tokens';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Empty } from '@prophecy/ui-v3/Empty';
import { Input } from '@prophecy/ui-v3/Input';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { Text } from '@prophecy/ui-v3/Typography';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { ChatActionsMenu } from './ChatActionsMenu';
import { ChatTitleInput } from './ChatTitleInput';
import { HOME_CHAT_TEST_IDS, NEW_CONVERSATION_NAME } from './constants';

import type { HomeConversation } from './api';
import type React from 'react';

const HEAD_ICON_SIZE = '18px';

const Head = styled(Stack)`
  .${ICON_CLASS} {
    width: ${HEAD_ICON_SIZE};
    height: ${HEAD_ICON_SIZE};
  }
`;

const RowMenu = styled(Stack)``;

const Row = styled(Stack)<{ $selected: boolean }>`
  width: 100%;
  border-radius: ${theme.radius.x6};
  background: ${({ $selected }) => ($selected ? theme.colors.gray100 : 'transparent')};

  ${RowMenu} {
    visibility: hidden;
  }

  &:hover,
  &:focus-within {
    background: ${({ $selected }) => ($selected ? theme.colors.gray100 : theme.colors.gray50)};

    ${RowMenu} {
      visibility: visible;
    }
  }
`;

const RowButton = styled(Button)`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  height: auto;
  justify-content: flex-start;
  gap: ${theme.spaces.x6};
  padding: ${theme.spaces.x4} ${theme.spaces.x10};
`;

const RenameContainer = styled(Stack)`
  flex: 1;
  min-width: 0;
  padding: ${theme.spaces.x4} ${theme.spaces.x10};
`;

const ListScroller = styled(Stack)`
  min-height: 0;
  overflow-y: auto;
`;

const SectionTitle = styled(Stack)`
  padding: ${theme.spaces.x2} ${theme.spaces.x10};
`;

function ConversationRow({
  conversation,
  selected,
  pinned,
  renaming,
  deleting,
  onSelect,
  onRename,
  onDelete,
  onTogglePin
}: {
  conversation: HomeConversation;
  selected: boolean;
  pinned: boolean;
  renaming: boolean;
  deleting: boolean;
  onSelect: (conversationId: string) => void;
  onRename: (conversationId: string, name: string) => Promise<void>;
  onDelete: (conversationId: string) => Promise<void>;
  onTogglePin: (conversationId: string) => void;
}) {
  const name = conversation.name || NEW_CONVERSATION_NAME;
  const rename = useInlineRename({
    currentName: name,
    onRename: (nextName) => void onRename(conversation.conversationId, nextName),
    disabled: renaming || deleting
  });

  return (
    <Row
      $selected={selected || rename.isEditing}
      direction='horizontal'
      alignY='center'
      gap={theme.spaces.x6}
      data-test-id={HOME_CHAT_TEST_IDS.conversationRow}>
      {rename.isEditing ? (
        <RenameContainer>
          <ChatTitleInput
            value={rename.value}
            weight={theme.fontWeight.medium}
            tone={theme.colors.gray900}
            onChange={rename.setValue}
            onSubmit={rename.submit}
            onKeyDown={rename.onKeyDown}
            data-test-id={HOME_CHAT_TEST_IDS.renameInput}
          />
        </RenameContainer>
      ) : (
        <RowButton
          variant='plain'
          aria-current={selected ? 'true' : undefined}
          data-test-id={HOME_CHAT_TEST_IDS.conversationOpen}
          onClick={() => onSelect(conversation.conversationId)}
          onDoubleClick={renaming || deleting ? undefined : rename.start}>
          <Ellipsis tooltip>
            <Text
              level='sm'
              weight={selected ? theme.fontWeight.medium : theme.fontWeight.normal}
              tone={selected ? theme.colors.gray900 : theme.colors.gray700}>
              {name}
            </Text>
          </Ellipsis>
        </RowButton>
      )}
      <RowMenu padding={`0 ${theme.spaces.x10} 0 0`}>
        <ChatActionsMenu
          name={name}
          pinned={pinned}
          renaming={renaming}
          deleting={deleting}
          onRename={rename.start}
          onTogglePin={() => onTogglePin(conversation.conversationId)}
          onDelete={() => onDelete(conversation.conversationId)}
        />
      </RowMenu>
    </Row>
  );
}

function Section({ title, testId, children }: { title: string; testId?: string; children: React.ReactNode }) {
  return (
    <Stack gap={theme.spaces.x4} width='100%' data-test-id={testId}>
      <SectionTitle>
        <Text level='xs' weight={theme.fontWeight.medium} tone={theme.colors.gray500}>
          {title}
        </Text>
      </SectionTitle>
      {children}
    </Stack>
  );
}

export function ConversationList({
  conversations,
  selectedId,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  pinnedIds,
  renamingId,
  deletingId,
  onLoadMore,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
  onTogglePin
}: {
  conversations: HomeConversation[];
  selectedId: string | undefined;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  pinnedIds: string[];
  /** The one conversation currently being renamed/deleted, if any — disables its own row's controls. */
  renamingId?: string;
  deletingId?: string;
  onLoadMore: () => void;
  onSelect: (conversationId: string) => void;
  onNewChat: () => void;
  onRename: (conversationId: string, name: string) => Promise<void>;
  onDelete: (conversationId: string) => Promise<void>;
  onTogglePin: (conversationId: string) => void;
}) {
  const [search, setSearch] = useState('');

  const { pinned, others } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matching = needle
      ? conversations.filter((c) => (c.name || NEW_CONVERSATION_NAME).toLowerCase().includes(needle))
      : conversations;
    const pinnedSet = new Set(pinnedIds);
    return {
      pinned: matching.filter((c) => pinnedSet.has(c.conversationId)),
      others: matching.filter((c) => !pinnedSet.has(c.conversationId))
    };
  }, [conversations, pinnedIds, search]);

  const renderRow = (conversation: HomeConversation) => (
    <ConversationRow
      key={conversation.conversationId}
      conversation={conversation}
      selected={conversation.conversationId === selectedId}
      pinned={pinnedIds.includes(conversation.conversationId)}
      renaming={renamingId === conversation.conversationId}
      deleting={deletingId === conversation.conversationId}
      onSelect={onSelect}
      onRename={onRename}
      onDelete={onDelete}
      onTogglePin={onTogglePin}
    />
  );

  // Search only filters the pages already fetched, so "no matches" must still offer the next one —
  // otherwise a chat on page 2 is unreachable and the empty state is a dead end.
  const loadMore = hasNextPage ? (
    <Button
      variant='linkGray'
      size='s'
      loading={isFetchingNextPage}
      onClick={onLoadMore}
      data-test-id={HOME_CHAT_TEST_IDS.loadMoreConversations}>
      Load more
    </Button>
  ) : null;

  let body: React.ReactNode;
  if (isLoading) {
    body = <Spinner spinning size='m' />;
  } else if (conversations.length === 0) {
    body = <Empty size='s' description='No chats yet' />;
  } else if (pinned.length === 0 && others.length === 0) {
    body = (
      <Stack gap={theme.spaces.x12} width='100%' alignY='center'>
        <Empty size='s' description='No chats match your search' />
        {loadMore}
      </Stack>
    );
  } else {
    body = (
      <Stack gap={theme.spaces.x12} width='100%'>
        {pinned.length > 0 ? (
          <Section title='Pinned' testId={HOME_CHAT_TEST_IDS.pinnedSection}>
            {pinned.map(renderRow)}
          </Section>
        ) : null}
        {others.length > 0 ? <Section title='Chats'>{others.map(renderRow)}</Section> : null}
        {loadMore}
      </Stack>
    );
  }

  const isNewChat = !selectedId;

  return (
    <Stack height='100%' width='100%'>
      <Head direction='horizontal' alignY='center' gap={theme.spaces.x8} padding={theme.spaces.x16}>
        <MessageChatSquareIcon type='default' size='xs' color={theme.colors.gray900} />
        <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
          DataGPT
        </Text>
      </Head>
      <Stack padding={`0 ${theme.spaces.x8}`} width='100%'>
        <Input
          inputSize='s'
          placeholder='Search chats'
          value={search}
          onChange={setSearch}
          icon={<SearchMdIcon type='default' size='xs' />}
          iconPlacement='left'
          data-test-id={HOME_CHAT_TEST_IDS.searchInput}
        />
      </Stack>
      <ListScroller
        gap={theme.spaces.x8}
        grow='1'
        shrink='1'
        width='100%'
        padding={`${theme.spaces.x12} ${theme.spaces.x8}`}>
        <Row $selected={isNewChat} direction='horizontal' alignY='center'>
          <RowButton
            variant='plain'
            aria-current={isNewChat ? 'true' : undefined}
            icon={<PlusIcon type='default' size='xs' color={theme.colors.gray500} />}
            iconPlacement='left'
            onClick={onNewChat}
            data-test-id={HOME_CHAT_TEST_IDS.newChatButton}>
            <Text
              level='sm'
              weight={isNewChat ? theme.fontWeight.medium : theme.fontWeight.normal}
              tone={isNewChat ? theme.colors.gray900 : theme.colors.gray700}>
              New chat
            </Text>
          </RowButton>
        </Row>
        {body}
      </ListScroller>
    </Stack>
  );
}
